import { useState, useEffect, useCallback, useRef } from "react"
import { useAuth } from "@/hooks/useAuth"
import { searchAuditLogs, type AuditEvent } from "@/services/adminService"

interface UseAuditLogsOptions {
  userId?: string
  eventType?: string
  from?: number
  to?: number
  limit?: number
}

export function useAuditLogs(options: UseAuditLogsOptions = {}) {
  const { token } = useAuth()
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nextCursorRef = useRef<string | null>(null)

  const refresh = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const data = await searchAuditLogs(token, { ...options, cursor: undefined })
      const sorted = (data.events || []).sort(
        (a: AuditEvent, b: AuditEvent) => (b.timestamp || 0) - (a.timestamp || 0)
      )
      setEvents(sorted)
      setCount(data.count || 0)
      nextCursorRef.current = data.nextCursor || null
      setHasMore(data.nextCursor !== null)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load audit logs")
    } finally {
      setLoading(false)
    }
  }, [token, options.userId, options.eventType, options.from, options.to, options.limit])

  const loadMore = useCallback(async () => {
    if (!token || !nextCursorRef.current || loadingMore) return
    setLoadingMore(true)
    try {
      const data = await searchAuditLogs(token, {
        ...options,
        cursor: nextCursorRef.current,
      })
      setEvents((prev) => {
        const combined = [...prev, ...(data.events || [])]
        return combined.sort(
          (a: AuditEvent, b: AuditEvent) => (b.timestamp || 0) - (a.timestamp || 0)
        )
      })
      setCount((prev) => prev + (data.count || 0))
      nextCursorRef.current = data.nextCursor || null
      setHasMore(data.nextCursor !== null)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load more audit logs")
    } finally {
      setLoadingMore(false)
    }
  }, [token, loadingMore, options.userId, options.eventType, options.from, options.to, options.limit])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { events, count, loading, loadingMore, hasMore, error, refresh, loadMore }
}
